import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../lib/utils';
import { Slot } from 'radix-ui';

const buttonVariants = cva(
  "inline-flex shrink-0 items-center justify-center gap-2 rounded-md text-sm font-medium whitespace-nowrap transition-all focus-ring disabled:pointer-events-none disabled:opacity-50 aria-invalid:border-destructive [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-(--icon-sm)",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive: 'border border-danger bg-danger-muted text-danger hover:bg-danger-muted/80',
        outline: 'border bg-surface shadow-sm hover:bg-surface-muted hover:text-text',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-surface-muted hover:text-text',
        link: 'text-accent-strong underline-offset-4 hover:underline',
      },
      size: {
        default: 'min-h-(--target-min) px-4 py-2 has-[>svg]:px-3',
        xs: 'min-h-(--target-min) gap-1 rounded-md px-2 text-xs has-[>svg]:px-1',
        sm: 'min-h-(--target-min) gap-1 rounded-md px-3 has-[>svg]:px-2',
        lg: 'min-h-(--target-min) rounded-md px-6 has-[>svg]:px-4',
        icon: 'min-h-(--target-min) min-w-(--target-min)',
        'icon-xs': 'min-h-(--target-min) min-w-(--target-min) rounded-md',
        'icon-sm': 'min-h-(--target-min) min-w-(--target-min)',
        'icon-lg': 'min-h-(--target-min) min-w-(--target-min)',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

export { Button, buttonVariants };
